import PropTypes from "prop-types";
import React, { useContext, useState } from "react";

import { useAppSetting } from "@swa-ui/application";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { SMALL, useDeviceInfo, window } from "@swa-ui/browser";
import { Link, PageGrid } from "@swa-ui/core";
import { dateFormats, swaDate } from "@swa-ui/date";
import {
  CalendarFormField,
  Form,
  MultiselectFormField,
  RecentSearchContext,
  RequiredIndicator,
  SelectFormField,
  SubmitFormField,
  TimeFormField,
  useForm,
  useRecentSearches,
  yupResolver,
} from "@swa-ui/form";
import { useSearchParams } from "@swa-ui/href";
import { AutocompleteLocationsFormField, carLocationMethods } from "@swa-ui/itinerary";
import i18n from "@swa-ui/locale";
import { usePersistedState } from "@swa-ui/persistence";
import { classNames } from "@swa-ui/string";

import { INT_CAR_BOOKING_WIDGET } from "../../defines/intParameters";
import { SEARCH_TYPE_CAR } from "../../defines/searchType";
import { NO_PREFERENCE } from "../../defines/vendorGroups";
import { carBookingFormSchema } from "../../schema/carBookingFormSchema";
import { RecentSearchFormField } from "../RecentSearchFormField";
import styles from "./CarBookingForm.module.scss";
import { RentalCarFormField } from "./RentalCarFormField";
import { getCarBookingSearchHref } from "./utilities/getCarBookingSearchHref";
import { getDefaultDate } from "./utilities/getDefaultDate";

const DEFAULT_TIME = "11:00am";
const WAIT_FOR_CALENDAR_TO_CLOSE = 100;

export const CarBookingForm = (props) => {
  const { defaultFormDataOverride } = props || {};
  const searchParamsFromURL = useSearchParams() || {};
  const disableCarBookingSearchParams = useAppSetting("disableCarBookingSearchParams");
  const defaultFormData = getDefaultFormData();
  const { addRecentSearch } = useContext(RecentSearchContext);
  const { CAR_BOOKING_INDEX } = getBootstrapData("urls");
  const { recentSearches } = useRecentSearches({ filterByTypes: SEARCH_TYPE_CAR });
  const methods = useForm({ resolver: yupResolver(carBookingFormSchema()) });
  const { setValue, trigger } = methods;
  const { noPreferenceCarTypeKey, typeOrder } = getBootstrapData("car/car-types-data");
  const { screenSize } = useDeviceInfo();
  const [calendarSize, setCalendarSize] = useState("");
  const [formData, setFormData] = usePersistedState(defaultFormData);
  const [justClosedCalendar, setJustClosedCalendar] = useState(false);
  const [noVehicleSizePreferenceSet, setNoVehicleSizePreferenceSet] = useState(true);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const carLastBookableDaysRange = useAppSetting("carLastBookableDaysRange");

  return (
    <div className={styles.container}>
      <PageGrid className={styles.layout}>
        <Form methods={methods} onSubmit={handleSubmit}>
          <RequiredIndicator className={styles.requiredIndicator} />
          <RecentSearchFormField {...getRecentSearchesProps()} />
          <AutocompleteLocationsFormField {...getPickupLocationProps()} />
          <AutocompleteLocationsFormField {...getReturnLocationProps()} />
          <CalendarFormField {...getPickupDateProps()} />
          <TimeFormField {...getPickupTimeProps()} />
          <CalendarFormField {...getReturnDateProps()} />
          <TimeFormField {...getReturnTimeProps()} />
          <SelectFormField {...getDriverAgeProps()} />
          <RentalCarFormField {...getRentalCompanyProps()} />
          <MultiselectFormField {...getVehicleSizeProps()} />
          <SubmitFormField {...getSubmitButtonProps()}>
            {i18n("CarBookingForm__SUBMIT_BUTTON")}
          </SubmitFormField>
        </Form>
        <Link {...getAdvancedSearchProps()}>{i18n("CarBookingForm__ADVANCED_SEARCH_LINK")}</Link>
      </PageGrid>
    </div>
  );

  function getRecentSearchesProps() {
    return {
      className: styles.recentSearches,
      componentProps: {
        list: getFilteredRecentSearches(),
        onChange: handleRecentSearchSelection,
        searchType: SEARCH_TYPE_CAR,
      },
      disabled: searchInProgress,
      hideBottomSection: true,
      label: i18n("CarBookingForm__RECENT_SEARCHES_LABEL"),
      name: "recentSearch",
    };
  }

  function getPickupLocationProps() {
    const { pickupLocation } = formData;

    return {
      "aria-label": i18n("CarBookingForm__PICKUP_LOCATION_ARIA"),
      caption: pickupLocation ? carLocationMethods.getCarLocationName(pickupLocation) : undefined,
      className: styles.pickupLocation,
      componentProps: {
        defaultValue: pickupLocation,
        labelText: i18n("CarBookingForm__PICKUP_LOCATION_LABEL"),
        noMatchFoundText: i18n("CarBookingForm__NO_MATCH_FOUND"),
        onChange: handlePickupLocationChange,
        required: true,
        softEdge: true,
      },
      disabled: searchInProgress,
      label: i18n("CarBookingForm__PICKUP_LOCATION_LABEL"),
      name: "pickupLocation",
    };
  }

  function getReturnLocationProps() {
    const { returnLocation } = formData;

    return {
      "aria-label": i18n("CarBookingForm__RETURN_LOCATION_ARIA"),
      caption: returnLocation ? carLocationMethods.getCarLocationName(returnLocation) : undefined,
      className: styles.returnLocation,
      componentProps: {
        defaultValue: returnLocation,
        labelText: i18n("CarBookingForm__RETURN_LOCATION_LABEL"),
        noMatchFoundText: i18n("CarBookingForm__NO_MATCH_FOUND"),
        onChange: (event) => updateFormData({ returnLocation: event.target.value }),
        required: true,
        softEdge: true,
      },
      disabled: searchInProgress,
      label: i18n("CarBookingForm__RETURN_LOCATION_LABEL"),
      name: "returnLocation",
    };
  }

  function getPickupDateProps() {
    const { pickupDate } = formData;

    return {
      caption: getDateCaption(pickupDate),
      className: styles.pickupDate,
      componentProps: getCalendarProps(i18n("CarBookingForm__PICKUP_DATE_ARIA"), "start"),
      disabled: searchInProgress,
      label: i18n("CarBookingForm__PICKUP_DATE_LABEL"),
      name: "pickupDate",
    };
  }

  function getPickupTimeProps() {
    return {
      className: styles.pickupTime,
      componentProps: {
        "aria-label": i18n("CarBookingForm__PICKUP_TIME_ARIA"),
        defaultValue: formData.pickupTime,
        maxItemsToDisplay: 7,
        onChange: (event) => {
          updateFormData({ pickupTime: event.target.value });
        },
        required: true,
      },
      disabled: searchInProgress,
      label: i18n("CarBookingForm__PICKUP_TIME_LABEL"),
      name: "pickupTime",
    };
  }

  function getReturnDateProps() {
    const { returnDate } = formData;

    return {
      caption: getDateCaption(returnDate),
      className: styles.returnDate,
      componentProps: getCalendarProps(i18n("CarBookingForm__RETURN_DATE_ARIA"), "end"),
      disabled: searchInProgress,
      label: i18n("CarBookingForm__RETURN_DATE_LABEL"),
      name: "returnDate",
    };
  }

  function getReturnTimeProps() {
    return {
      className: styles.returnTime,
      componentProps: {
        "aria-label": i18n("CarBookingForm__RETURN_TIME_ARIA"),
        defaultValue: formData.returnTime,
        maxItemsToDisplay: 7,
        onChange: (event) => {
          updateFormData({ returnTime: event.target.value });
        },
        required: true,
      },
      disabled: searchInProgress,
      label: i18n("CarBookingForm__RETURN_TIME_LABEL"),
      name: "returnTime",
    };
  }

  function getDriverAgeProps() {
    const { driverAge } = formData;

    return {
      className: styles.driverAge,
      componentProps: {
        defaultValue: driverAge,
        list: [
          {
            displayValue: i18n("CarBookingForm__DRIVING_AGE_25_AND_OLDER"),
            label: i18n("CarBookingForm__DRIVING_AGE_25_AND_OLDER"),
            value: "25",
          },
          {
            displayValue: i18n("CarBookingForm__DRIVING_AGE_UNDER_25"),
            label: i18n("CarBookingForm__DRIVING_AGE_UNDER_25"),
            value: "24",
          },
        ],
        minimumListWidth: "full-width",
        onChange: (event) => {
          setValue("driverAge", event.target.value);
          updateFormData({ driverAge: event.target.value });
        },
      },
      label: i18n("CarBookingForm__DRIVING_AGE_LABEL"),
      labelInformationTipProps: {
        content: i18n("CarBookingForm__DRIVING_AGE_TOOLTIP"),
        location: "above",
        size: "small",
        width: "xsmall",
      },
      name: "driverAge",
    };
  }

  function getRentalCompanyProps() {
    return {
      className: styles.rentalCompany,
      disabled: searchInProgress,
      rentalCompany: formData.rentalCompany,
      setValue,
      setRentalVehicleInfo: updateFormData,
    };
  }

  function getVehicleSizeProps() {
    return {
      className: styles.vehicleSize,
      componentProps: {
        "aria-label": i18n("CarBookingForm__VEHICLE_SIZE"),
        fullScreen: screenSize === SMALL,
        getSelectionValueText: getSelectionValueText,
        list: getVehicleSizeList(),
        maxItemsToDisplay: 10,
        minimumListWidth: "full-width",
        onChange: (event) => {
          handleVehicleSizePreferenceChange(event.target.value);
        },
        value: formData.vehicleSize,
      },
      disabled: searchInProgress,
      hideBottomSection: true,
      label: i18n("CarBookingForm__VEHICLE_SIZE"),
      name: "vehicleSize",
    };
  }

  function getSubmitButtonProps() {
    return {
      className: styles.submitButton,
      componentProps: {
        fullWidth: true,
        styleType: "primary",
        submitInProgress: searchInProgress,
      },
      hideBottomSection: true,
      name: "submit",
    };
  }

  function getFormDataConfig({
    carTypeOverride,
    driverAgeOverride,
    pickupDateOverride,
    pickupLocationOverride,
    pickupTimeOverride,
    returnDateOverride,
    returnLocationOverride,
    returnTimeOverride,
    vendorsOverride,
  }) {
    return {
      defaultValue: {
        driverAge: driverAgeOverride || "25",
        pickupDate: pickupDateOverride
          ? swaDate(pickupDateOverride).format(dateFormats.default)
          : getDefaultDate("pickupDate", dateFormats.default),
        pickupLocation: pickupLocationOverride || "",
        pickupTime: pickupTimeOverride || DEFAULT_TIME,
        rentalCompany: getValueAsArray(vendorsOverride),
        returnDate: returnDateOverride
          ? swaDate(returnDateOverride).format(dateFormats.default)
          : getDefaultDate("returnDate", dateFormats.default),
        returnLocation: returnLocationOverride || "",
        returnTime: returnTimeOverride || DEFAULT_TIME,
        vehicleSize: getValueAsArray(carTypeOverride),
        searchParams: defaultFormDataOverride?.searchParams ?? {},
      },
      key: "CarBookingFormValues",
      timeToLiveInMinutes: 30,
    };
  }

  function getValueAsArray(override) {
    const result = [NO_PREFERENCE];

    if (override) {
      result.pop();

      if (Array.isArray(override)) {
        result.push(...override);
      } else {
        result.push(override);
      }
    }

    return result;
  }

  function getDefaultFormData() {
    const carSearchParams = {
      ...defaultFormDataOverride,
      ...(!disableCarBookingSearchParams && searchParamsFromURL.searchParams),
    };

    const {
      carType: carTypeOverride,
      driverAge: driverAgeOverride,
      pickUpDate: pickupDateOverride,
      pickUpLocation: pickupLocationOverride,
      pickUpTime: pickupTimeOverride,
      returnDate: returnDateOverride,
      returnLocation: returnLocationOverride,
      returnTime: returnTimeOverride,
      vendors: vendorsOverride,
    } = carSearchParams;

    return getFormDataConfig({
      carTypeOverride,
      driverAgeOverride,
      pickupDateOverride,
      pickupLocationOverride,
      pickupTimeOverride,
      returnDateOverride,
      returnLocationOverride,
      returnTimeOverride,
      vendorsOverride,
    });
  }

  function getAdvancedSearchProps() {
    return {
      className: styles.advancedSearchLink,
      emphasis: false,
      href: CAR_BOOKING_INDEX,
      paddingForFocus: false,
      size: "fontSize16",
    };
  }

  function getCalendarProps(ariaLabel, focusField) {
    const { pickupDate, returnDate } = formData;
    const isPickupDate = focusField === "start";

    return {
      "aria-label": ariaLabel,
      closeContent: i18n("CarBookingForm__CLOSE"),
      dateEnd: returnDate,
      dateStart: pickupDate,
      daysOfWeekInitials: i18n("CarBookingForm__DAYS_OF_WEEK_INITIALS"),
      defaultValue: isPickupDate ? pickupDate : returnDate,
      departText: i18n("CarBookingForm__DEPART_BUTTON_TEXT"),
      focusField,
      format: i18n("CarBookingForm__SHORT_DATE_MONTH_AND_DAY_FORMAT"),
      lastBookableDate: getLastBookableDate(),
      numberCalendars: 2,
      numberDates: 2,
      onClose: handleCalendarClose,
      onSelectEnd: handleSelectEnd,
      onSelectStart: handleSelectStart,
      onSetupEnd: handleCalendarSetupEnd,
      openOnFocus: isLargeCalendar() || !justClosedCalendar,
      required: true,
      returnText: i18n("CarBookingForm__RETURN_BUTTON_TEXT"),
      selectDateText: i18n("CarBookingForm__SELECT_DATE_TEXT"),
      shouldLeaveOpen: !isLargeCalendar(),
      title: isPickupDate
        ? i18n("CarBookingForm__PICKUP_DATE_SELECTOR_TITLE")
        : i18n("CarBookingForm__RETURN_DATE_SELECTOR_TITLE"),
    };
  }

  function getVehicleSizeList() {
    return [
      {
        label: i18n("CarBookingForm__TEXT_ALL"),
        value: noPreferenceCarTypeKey,
      },
      ...typeOrder.map((carType) => ({
        label: i18n(`CarBookingForm__${carType}`),
        value: carType,
      })),
    ];
  }

  function getSelectionValueText(values) {
    let valueText;

    if (values.indexOf(NO_PREFERENCE) !== -1 || values === "") {
      valueText = `${i18n("CarBookingForm__TEXT_ALL")}${values !== NO_PREFERENCE ? " " : ""}`;
    } else {
      valueText = `${values.length} selected`;
    }

    return valueText;
  }

  function getLastBookableDate() {
    return swaDate()
      .set("date", swaDate().get("date") + carLastBookableDaysRange)
      .format(i18n("CarBookingForm__DATE_FORMAT"));
  }

  function getDateCaption(dateString) {
    return dateString
      ? swaDate(dateString).format(i18n("CarBookingForm__DAY_OF_WEEK_MONTH_AND_YEAR"))
      : "";
  }

  function handleRecentSearchSelection(index) {
    const selectedRecentSearchData = recentSearches[index].formData;

    updateFormData(selectedRecentSearchData);
    setValue("pickupLocation", selectedRecentSearchData.pickupLocation);
    setValue("returnLocation", selectedRecentSearchData.returnLocation);
    setValue("pickupTime", selectedRecentSearchData.pickupTime);
    setValue("returnTime", selectedRecentSearchData.returnTime);
    setValue("driverAge", selectedRecentSearchData.driverAge);
    setValue("rentalCompany", selectedRecentSearchData.rentalCompany);
    setValue("vehicleSize", selectedRecentSearchData.vehicleSize);
  }

  function handlePickupLocationChange(event) {
    const pickupLocation = event.target.value;
    const returnLocation =
      formData.returnLocation === "" ? pickupLocation : formData.returnLocation;

    if (pickupLocation) {
      const [newPickupLocation, newReturnLocation] = [pickupLocation, returnLocation];

      updateFormData({
        pickupLocation: newPickupLocation,
        returnLocation: newReturnLocation,
      });

      setValue("pickupLocation", newPickupLocation);
      setValue("returnLocation", newReturnLocation);

      trigger("returnLocation");
    }
  }

  function handleSelectEnd(value) {
    const { pickupDate } = formData;
    const newFormData = { pickupDate };
    let selectionError = false;

    if (swaDate(pickupDate).isAfter(value, "day")) {
      newFormData.pickupDate = value;
      newFormData.returnDate = "";
      setValue("pickupDate", value);
      setValue("returnDate", "");
      selectionError = true;
    } else {
      newFormData.returnDate = value;
      setValue("returnDate", value);
    }

    updateFormData(newFormData);

    return selectionError;
  }

  function handleSelectStart(value) {
    const { returnDate } = formData;
    const newFormData = { returnDate };

    if (swaDate(value).isAfter(returnDate, "day")) {
      newFormData.returnDate = "";
      setValue("returnDate", "");
    }

    setValue("pickupDate", value);
    newFormData.pickupDate = value;

    updateFormData(newFormData);
  }

  function handleCalendarClose() {
    setJustClosedCalendar(true);
    setTimeout(() => {
      setJustClosedCalendar(false);
    }, WAIT_FOR_CALENDAR_TO_CLOSE);
  }

  function handleCalendarSetupEnd(size) {
    setCalendarSize(size);
  }

  function handleVehicleSizePreferenceChange(vehicleSize) {
    let result;

    if (vehicleSize.length === 0 || isNoVehicleSizePreferenceAdded(vehicleSize)) {
      result = [noPreferenceCarTypeKey];
    } else {
      result =
        vehicleSize.includes(noPreferenceCarTypeKey) && vehicleSize.length > 1
          ? vehicleSize.filter((item) => item !== noPreferenceCarTypeKey)
          : vehicleSize;
    }

    setNoVehicleSizePreferenceSet(result.includes(noPreferenceCarTypeKey));
    setValue("vehicleSize", result);
    updateFormData({ vehicleSize: result });
  }

  function handleSubmit() {
    const newFormData = { ...formData, int: INT_CAR_BOOKING_WIDGET };

    setSearchInProgress(!searchInProgress);
    addRecentSearch(newFormData, SEARCH_TYPE_CAR);
    window.location.assign(getCarBookingSearchHref(newFormData));
  }

  function isNoVehicleSizePreferenceAdded(newValue) {
    return newValue.indexOf(NO_PREFERENCE) !== -1 && !noVehicleSizePreferenceSet;
  }

  function getFilteredRecentSearches() {
    return recentSearches.map((search) => {
      const { pickupDate, pickupLocation, returnDate, returnLocation } = search.formData;

      return {
        departureDate: pickupDate,
        returnDate: returnDate,
        station1: pickupLocation,
        station2: returnLocation,
      };
    });
  }

  function updateFormData(newFormData) {
    setFormData((prevFormData) => ({ ...prevFormData, ...newFormData }));
  }

  function isLargeCalendar() {
    return calendarSize === "large";
  }
};

CarBookingForm.propTypes = {
  /** Consumers of this component can optionally set default values for form fields using this prop.
   *  For example, a static page driven by WCM data could use this prop. */
  defaultFormDataOverride: PropTypes.shape({
    driverAge: PropTypes.string,
    pickupDate: PropTypes.string,
    pickupLocation: PropTypes.string,
    rentalCompany: PropTypes.string,
    returnDate: PropTypes.string,
    returnLocation: PropTypes.string,
    vehicleSize: PropTypes.arrayOf(PropTypes.string),
    searchParams: PropTypes.object,
  }),
};
